import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FoodGroup, FoodGroupType } from "client/jspPlatformExperiment";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";

interface FoodGroupsState {
  /** Food groups shown on the management list page */
  groups: FoodGroup[];
  isGroupsLoading: boolean;
  /** Group detail (with members) for the recipe edit page / edit dialog */
  currentGroup: FoodGroup | null;
  isCurrentGroupLoading: boolean;
  isSaving: boolean;
  error: string | null | undefined;
}

const initialState: FoodGroupsState = {
  groups: [],
  isGroupsLoading: false,
  currentGroup: null,
  isCurrentGroupLoading: false,
  isSaving: false,
  error: null,
};

const getErrorMessage = (error: unknown): string => {
  const err = isApiError(error) ? toJspApiError(error) : null;
  if (err && isErrorResponseBodyType(err.body)) {
    return err.body.detail;
  }
  return "Unknown error!";
};

const fetchFoodGroups = createAsyncThunk(
  "foodGroups/fetchFoodGroups",
  async (input: { nameContains?: string } | undefined, { rejectWithValue }) =>
    jspPlatformApi()
      .foodGroup.getFoodGroup({
        query: { group_name_contains: input?.nameContains || undefined },
      })
      .then((res) => res.data ?? [])
      .catch((error) => rejectWithValue(getErrorMessage(error))),
);

const fetchFoodGroupDetail = createAsyncThunk(
  "foodGroups/fetchFoodGroupDetail",
  async (input: { groupId: number }, { rejectWithValue }) =>
    jspPlatformApi()
      .foodGroup.getFoodGroupByGroupId({
        path: { group_id: input.groupId },
      })
      .then((res) => res.data ?? null)
      .catch((error) => rejectWithValue(getErrorMessage(error))),
);

/**
 * Create a food group and optionally link its initial members.
 */
const createFoodGroup = createAsyncThunk(
  "foodGroups/createFoodGroup",
  async (
    input: { groupName: string; foodIds?: number[] },
    { rejectWithValue },
  ) => {
    try {
      const createRes = await jspPlatformApi().foodGroup.postFoodGroup({
        body: {
          group_name: input.groupName,
          group_type: FoodGroupType.SITE_DISH,
        },
      });
      const created = createRes.data;
      if (!created) {
        return null;
      }
      if (!input.foodIds || input.foodIds.length === 0) {
        return created;
      }
      const syncRes =
        await jspPlatformApi().foodGroup.putFoodGroupByGroupIdMembers({
          path: { group_id: created.id },
          body: { food_ids: input.foodIds },
        });
      return syncRes.data ?? created;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

const renameFoodGroup = createAsyncThunk(
  "foodGroups/renameFoodGroup",
  async (input: { groupId: number; groupName: string }, { rejectWithValue }) =>
    jspPlatformApi()
      .foodGroup.patchFoodGroupByGroupId({
        path: { group_id: input.groupId },
        body: { group_name: input.groupName },
      })
      .then((res) => res.data ?? null)
      .catch((error) => rejectWithValue(getErrorMessage(error))),
);

const deleteFoodGroup = createAsyncThunk(
  "foodGroups/deleteFoodGroup",
  async (input: { groupId: number }, { rejectWithValue }) =>
    jspPlatformApi()
      .foodGroup.deleteFoodGroupByGroupId({
        path: { group_id: input.groupId },
      })
      .then(() => input.groupId)
      .catch((error) => rejectWithValue(getErrorMessage(error))),
);

/**
 * Sync a group's members to an exact set of food ids.
 */
const syncFoodGroupMembers = createAsyncThunk(
  "foodGroups/syncFoodGroupMembers",
  async (input: { groupId: number; foodIds: number[] }, { rejectWithValue }) =>
    jspPlatformApi()
      .foodGroup.putFoodGroupByGroupIdMembers({
        path: { group_id: input.groupId },
        body: { food_ids: input.foodIds },
      })
      .then((res) => res.data ?? null)
      .catch((error) => rejectWithValue(getErrorMessage(error))),
);

/**
 * Merge a saved group into the list and the current-group slot.
 */
const mergeSavedGroup = (
  state: FoodGroupsState,
  saved: FoodGroup | null,
): FoodGroupsState => {
  if (!saved) {
    return { ...state, isSaving: false };
  }
  const isListed = state.groups.some((group) => group.id === saved.id);
  return {
    ...state,
    isSaving: false,
    groups: isListed
      ? state.groups.map((group) => (group.id === saved.id ? saved : group))
      : [...state.groups, saved],
    currentGroup:
      state.currentGroup === null || state.currentGroup.id === saved.id
        ? saved
        : state.currentGroup,
  };
};

export const foodGroupsSlice = createSlice({
  name: "foodGroupsSlice",
  initialState,
  reducers: {
    setCurrentFoodGroup: (
      state,
      action: PayloadAction<FoodGroup | null>,
    ): FoodGroupsState => ({ ...state, currentGroup: action.payload }),
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFoodGroups.pending, (state) => ({
      ...state,
      isGroupsLoading: true,
      error: null,
    }));
    builder.addCase(fetchFoodGroups.fulfilled, (state, action) => ({
      ...state,
      isGroupsLoading: false,
      groups: action.payload ?? [],
    }));
    builder.addCase(fetchFoodGroups.rejected, (state, action) => ({
      ...state,
      isGroupsLoading: false,
      error: action.payload as string,
    }));
    builder.addCase(fetchFoodGroupDetail.pending, (state) => ({
      ...state,
      isCurrentGroupLoading: true,
      error: null,
    }));
    builder.addCase(fetchFoodGroupDetail.fulfilled, (state, action) => ({
      ...state,
      isCurrentGroupLoading: false,
      currentGroup: action.payload ?? null,
    }));
    builder.addCase(fetchFoodGroupDetail.rejected, (state, action) => ({
      ...state,
      isCurrentGroupLoading: false,
      error: action.payload as string,
    }));
    builder.addCase(createFoodGroup.pending, (state) => ({
      ...state,
      isSaving: true,
      error: null,
    }));
    builder.addCase(createFoodGroup.fulfilled, (state, action) =>
      mergeSavedGroup(state, action.payload ?? null),
    );
    builder.addCase(createFoodGroup.rejected, (state, action) => ({
      ...state,
      isSaving: false,
      error: action.payload as string,
    }));
    builder.addCase(renameFoodGroup.pending, (state) => ({
      ...state,
      isSaving: true,
      error: null,
    }));
    // Rename responses may omit members — only take the new name.
    builder.addCase(renameFoodGroup.fulfilled, (state, action) => {
      const saved = action.payload ?? null;
      if (!saved) {
        return { ...state, isSaving: false };
      }
      return {
        ...state,
        isSaving: false,
        groups: state.groups.map((group) =>
          group.id === saved.id
            ? { ...group, group_name: saved.group_name }
            : group,
        ),
        currentGroup:
          state.currentGroup?.id === saved.id
            ? { ...state.currentGroup, group_name: saved.group_name }
            : state.currentGroup,
      };
    });
    builder.addCase(renameFoodGroup.rejected, (state, action) => ({
      ...state,
      isSaving: false,
      error: action.payload as string,
    }));
    builder.addCase(deleteFoodGroup.pending, (state) => ({
      ...state,
      isSaving: true,
      error: null,
    }));
    builder.addCase(deleteFoodGroup.fulfilled, (state, action) => ({
      ...state,
      isSaving: false,
      groups: state.groups.filter((group) => group.id !== action.payload),
      currentGroup:
        state.currentGroup?.id === action.payload ? null : state.currentGroup,
    }));
    builder.addCase(deleteFoodGroup.rejected, (state, action) => ({
      ...state,
      isSaving: false,
      error: action.payload as string,
    }));
    builder.addCase(syncFoodGroupMembers.pending, (state) => ({
      ...state,
      isSaving: true,
      error: null,
    }));
    builder.addCase(syncFoodGroupMembers.fulfilled, (state, action) =>
      mergeSavedGroup(state, action.payload ?? null),
    );
    builder.addCase(syncFoodGroupMembers.rejected, (state, action) => ({
      ...state,
      isSaving: false,
      error: action.payload as string,
    }));
  },
});

export const foodGroupActions = {
  ...foodGroupsSlice.actions,
  fetchFoodGroups,
  fetchFoodGroupDetail,
  createFoodGroup,
  renameFoodGroup,
  deleteFoodGroup,
  syncFoodGroupMembers,
};

export const foodGroupsReducer = foodGroupsSlice.reducer;

export default foodGroupsReducer;
